import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import AsyncSelectField from "./AsyncSelectField";
import Fuse from "fuse.js";
import Tags from "venuescanner-js/services/tagService";
import isSSR from "venuescanner-js/services/isSSR";

const filterOpts = {
  shouldSort: true,
  threshold: 0.6,
  location: 0,
  distance: 100,
  maxPatternLength: 32,
  minMatchCharLength: 1,
  keys: [
    {
      name: "name",
      weight: 0.7,
    },
    {
      name: "searchable_by",
      weight: 0.3,
    },
  ],
};

const noOptionsMessage = () => "No results";

const excludedTags = ["Blank Canvas"];

export const mapTagToValue = ({ name, slug, searchable_by: aliases }) => ({
  value: slug,
  label: name,
  searchable_by: aliases,
  isCorporate: true,
});

let fuse;
let tags;

const fetchFuse = async () => {
  // if tags already retrieved and fuse constructed then return the fuse object
  if (typeof window === "undefined") return;
  if (fuse) return fuse;
  // Fetch and map the tags from the TagService
  const unsortedTags = (await Tags.fetch()).map((t) => {
    return {
      ...t,
      position: t.position * 1,
    };
  }).filter((tag)=> !excludedTags.includes(tag.name));

  tags = unsortedTags.sort((a, b) => {
    const posA = a.position * 1;
    const posB = b.position * 1;
    if ((isNaN(posA) || posA === 0) && (isNaN(posB) || posB === 0)) return 0;
    if (isNaN(posB) || posB === 0) return -1;
    if (isNaN(posA) || posA === 0) return 1;
    return posA - posB;
  });
  // apply the tag array to fuse
  fuse = new Fuse(tags, filterOpts);
  return fuse;
};

export const EventTypeField = React.forwardRef(
  (
    {
      value,
      absolute = false,
      tagData,
      isForm,
      onFocus,
      onChange,
      id,
      ...props
    },
    ref
  ) => {
    useEffect(() => {
      if (!fuse && !isSSR()) {
        fetchFuse()
          .then(() => {
            setDefaultOptions(tags.map(mapTagToValue));
          })
          .catch(console.error);
      }
    }, []);

    const [selectedValue, setSelectedValue] = useState();
    const [defaultOptions, setDefaultOptions] = useState();
    const loadData = (cb = () => {}) => {
      if (!fuse && !isSSR()) {
        fetchFuse().then(() => {
          setDefaultOptions(tags.map(mapTagToValue));
          setSelectedValue(
            tags.map(mapTagToValue).find((t) => t.value === value)
          );
          cb();
        });
      }
    };
    const changeHandler = (option) => {
      setSelectedValue(option);
      if (onChange) onChange(option);
    };
    const filter = async (input) => {
      const fuseFilter = await fetchFuse();
      const results = input ? fuseFilter.search(input) : tags;
      return results.map(mapTagToValue);
    };
    const getData = !!value;
    if (getData) loadData();
    return (
      <div>
        <AsyncSelectField
          inputProps={{ id, "data-testid": "eventTypeField" }}
          placeholder={"Event / Venue type"}
          noOptionsMessage={noOptionsMessage}
          value={selectedValue}
          absolute={absolute}
          onChange={changeHandler}
          ref={ref}
          defaultOptions={defaultOptions || false}
          getResults={filter}
          onFocus={() => {
            if (onFocus) onFocus();
            loadData();
            return true;
          }}
          {...props}
        />
      </div>
    );
  }
);

EventTypeField.displayName = "EventTypeField";

EventTypeField.propTypes = {
  tagData: PropTypes.array,
  absolute: PropTypes.bool,
  id: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string }),
  ]),
  onFocus: PropTypes.func,
  onChange: PropTypes.func,
  isForm: PropTypes.bool,
};

export default EventTypeField;
